import { Component, computed, inject, input, InputSignal, output, OutputEmitterRef, Signal, Type } from '@angular/core';

import { IMDDropDownOption, MDDropDownComponent } from '@melodic-ui/components';
import { RouterService } from '@melodic-ui/services';
import { LearnerUserStateService } from '../../state/learner-users-state.service';
import { UserDropDownOptionComponent } from './user-drop-down-option.component';

/**
 * The recurring-breadcrumb key the app shell registers this switcher under. Exported so a route can
 * name it in `data.hideRecurringBreadcrumbs` (the partner Reports pages do) without repeating the
 * literal.
 */
export const USER_DROPDOWN_CRUMB_KEY = 'user-dropdown';

export interface IUserDropDownOption {
	userID: number;
	userName: string;
	userImage: string;
	isActiveUser: boolean;
	hasActiveEnrollment: boolean;
}

@Component({
	selector: 'lrnr-user-dropdown',
	standalone: true,
	imports: [MDDropDownComponent],
	templateUrl: './user-drop-down.component.html',
	styleUrl: './user-drop-down.component.scss'
})
export class UserDropdownComponent {
	private _routerService: RouterService = inject(RouterService);
	private _learnerUserStateService = inject(LearnerUserStateService);

	public users = input.required<IUserDropDownOption[]>();

	public activeUsers = computed(() => {
		const currentRoute = this._routerService.activeRoute().snapshot.url;
		if (currentRoute && currentRoute.map((x) => x.path).includes('profile')) {
			return this.users();
		}

		return this.users().filter((x) => !x.isActiveUser);
	});
	public isReadonly = computed(() => this.activeUsers().length === 1);

	public activeLearnerUserID: Signal<number | undefined> = this._learnerUserStateService.activeLearnerUserID;

	// onChange: An input used when dynamically injecting this component into a breadcrumb item.
	public onChange: InputSignal<((user: IUserDropDownOption) => void) | undefined> = input<(user: IUserDropDownOption) => void>();

	// change: An output used to emit changes to the selected user. If this component is used outside the breadcrumb item, this output can be used to listen for changes.
	public change: OutputEmitterRef<IUserDropDownOption> = output<IUserDropDownOption>();

	public userOptions = computed<IMDDropDownOption[] | undefined>(() => {
		const activeLearnerUserID = this.activeLearnerUserID();

		if (activeLearnerUserID === undefined) {
			return undefined;
		}

		return this.activeUsers().map((user) => {
			return {
				value: user.userID,
				label: user.userName,
				icon: user.hasActiveEnrollment ? undefined : 'untitled-ui/users/user-x-02',
				selected: activeLearnerUserID === user.userID
			};
		});
	});

	public selectedUserID: Signal<number | undefined> = computed(() => this.userOptions()?.find((u) => u.selected)?.value as number | undefined);

	public userOptionComponent: Type<Component> = UserDropDownOptionComponent as Type<Component>;

	public changeHandler(user: unknown): void {
		const activeUsers = this.activeUsers();
		let selectedUser = activeUsers.find((u) => u.userID === user);

		if (!selectedUser) {
			selectedUser = activeUsers[0];
		}

		this.change.emit(selectedUser as IUserDropDownOption);

		if (this.onChange()) {
			(this.onChange() as (user: IUserDropDownOption) => void)(selectedUser as IUserDropDownOption);
		}
	}
}
